import type { InboxCaseBatchWorkflowItem } from "../../../../../../../../../../../../../api/kehrblatt/inbox-cases"

export type CustomerInformationStepOutput = {
  outcome: "informed" | "confirmation"
  workOrderId: string | null
  canceledScheduleEventId: string | null
  scheduledScheduleEventId: string | null
  contactOrderId: string | null
  firstContactStepId: string | null
  decision: string | null
  status: string | null
  message: string | null
  sent: boolean | null
  skipped: boolean | null
  reason: string | null
  prefilled: boolean | null
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const optionalString = (value: unknown): string | null => {
  const normalized = typeof value === "string" ? value.trim() : ""

  return normalized || null
}

const optionalBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null

export const parseCustomerInformationStepOutput = (
  value: unknown,
): CustomerInformationStepOutput | null => {
  const stepOutput = asRecord(value)
  const output = Object.prototype.hasOwnProperty.call(stepOutput, "customer_information")
    ? asRecord(stepOutput.customer_information)
    : stepOutput
  const rawOutcome = optionalString(output.outcome)
  const decision = optionalString(output.decision)
  const outcome =
    rawOutcome === "informed" || rawOutcome === "confirmation"
      ? rawOutcome
      : decision?.startsWith("CONFIRMATION")
        ? "confirmation"
        : decision === "INFORM"
          ? "informed"
          : null

  if (!outcome) return null

  const parsed: CustomerInformationStepOutput = {
    outcome,
    workOrderId: optionalString(output.work_order_id),
    canceledScheduleEventId: optionalString(output.canceled_schedule_event_id),
    scheduledScheduleEventId: optionalString(output.scheduled_schedule_event_id),
    contactOrderId: optionalString(output.contact_order_id),
    firstContactStepId: optionalString(output.first_contact_step_id),
    decision,
    status: optionalString(output.status),
    message: optionalString(output.message),
    sent: optionalBoolean(output.sent),
    skipped: optionalBoolean(output.skipped),
    reason: optionalString(output.reason),
    prefilled: optionalBoolean(output.prefilled),
  }

  if (parsed.outcome === "informed") {
    return parsed.contactOrderId ? parsed : null
  }

  const queued =
    parsed.status === "queued" || parsed.sent === true || parsed.decision === "CONFIRMATION_EMAIL"
  const skipped =
    parsed.status === "skipped" ||
    parsed.skipped === true ||
    parsed.decision === "CONFIRMATION_SKIPPED"

  return queued !== skipped ? parsed : null
}

export const customerInformationOutputForItem = (
  item: InboxCaseBatchWorkflowItem,
): CustomerInformationStepOutput | null => {
  const step = item.steps.find((candidate) => candidate.step_key === "inform_customer")

  return parseCustomerInformationStepOutput(step?.output)
}

export const projectPersistedCustomerInformation = ({
  output,
  actionCaseId,
  organizationId,
  workOrder,
  event,
  contactOrderReason,
}: {
  output: CustomerInformationStepOutput
  actionCaseId: string
  organizationId: string
  workOrder: WorkOrder
  event: ScheduleEvent
  contactOrderReason: "INITIAL" | "RESCHEDULE"
}): { event: ScheduleEvent; contactOrder: ContactOrder | null } => {
  if (output.outcome === "confirmation") {
    const queued =
      output.status === "queued" || output.sent === true || output.decision === "CONFIRMATION_EMAIL"
    const label =
      output.message ??
      (queued
        ? "Bestätigung wird per E-Mail übermittelt"
        : "Keine Bestätigung per E-Mail versendet")

    return {
      event: {
        ...event,
        customer_information_mode: queued ? "confirmation_queued" : "confirmation_skipped",
        customer_information_label: label,
        customer_information_detail: output.reason,
      } as ScheduleEvent,
      contactOrder: null,
    }
  }

  const contactOrderId = output.contactOrderId ?? `inbox-contact-order:${actionCaseId}`
  const scheduleEventId = output.scheduledScheduleEventId ?? event.id
  const assignedEmployeeIds = event.employee_id ? [event.employee_id] : []
  const contactOrder = {
    id: contactOrderId,
    organization_id: organizationId,
    case_id: null,
    work_order_id: output.workOrderId ?? workOrder.id,
    reason: contactOrderReason,
    status: "ACTIVE",
    schedule_event_id: scheduleEventId,
    proposed_start_at: event.start_at,
    proposed_end_at: event.end_at,
    assigned_employee_ids: assignedEmployeeIds,
    // The receipt proves the plan link, not a step's channel, state or execution settings.
    // Leave step details to the regular reload instead of inventing an executable step.
    product_refs: {
      work_order_id: output.workOrderId ?? workOrder.id,
      schedule_event_id: scheduleEventId,
      source_action_case_id: actionCaseId,
    },
  } as ContactOrder

  return {
    event: {
      ...event,
      contact_order_id: contactOrderId,
      customer_information_mode: "contact_order",
      customer_information_label: output.sent
        ? "Kundeninformation gesendet"
        : "Kundeninformation vorbereitet",
      customer_information_detail: output.message ?? output.reason,
    } as ScheduleEvent,
    contactOrder,
  }
}
